// 주석 이미지 표시(widget:'annotated-image') — 이미지 위에 사각형·폴리라인 오버레이를 겹쳐 그린다.
//
// 도메인 중립: 값 스키마(AnnotatedImageValue)는 "이미지 + 도형"만 담고, 무엇을 왜 표시하는지
// (위험요소·심각도 등)는 데이터 조립 쪽이 결정한다. 좌표는 0~1 정규화라 표시 크기와 무관하게 정합한다.
//
// 렌더 레이어 2종(구 안전점검보고서 HazardOverlay와 같은 결 — 2026-08 패리티 개편):
//  - 사각형(shapes): **HTML div 레이어**(left/top/width/height %). 점선·모서리 라운드·번호 배지가
//    픽셀 고정이어야 하는데, 이전의 viewBox 0..100 스트레치 svg에서는 rx가 축별로 왜곡되고 배지
//    (고정 px 박스+텍스트)를 얹을 수 없다. CSS border(dashed)는 px 고정이라 legacy와 동일 결.
//  - 수기선(strokes): 기존 svg(viewBox 0..100 스트레치 + vector-effect 픽셀 고정 선폭) 유지 —
//    폴리라인은 div로 표현 불가.
//
// rc 패키지 규약: 스타일은 전부 인라인 — 이 패키지에서만 처음 쓰는 Tailwind 유틸은 소비 앱의
// JIT가 생성하지 않아 조용히 무스타일이 된다(feedback-rc-package-tailwind-class-purge-inline-style).
//
// 결정: docs/decisions/2026-08-03-safety-inspection-composer-form.md

import type { CSSProperties, ReactNode } from 'react';
import type {
  AnnotatedImageShape,
  AnnotatedImageStroke,
  AnnotatedImageValue,
} from '@safetyhub/shared';
import { hexToRgba as hexToRgbaCss } from './resolver';
import { ImageWithFallback } from './ImageWithFallback';

/** 도형 색 미지정 시 기본선 색 — 도메인 무관 중립색(주의 환기용 red-500). */
export const ANNOTATED_IMAGE_DEFAULT_COLOR = '#ef4444';
/** 사각형 외곽선 두께 기본(px, 화면 고정) — admin 저작 UI(normalize·placeholder)와 공유. */
export const DEFAULT_STROKE_WIDTH = 2;
/** 값 없음 placeholder 박스 높이 — widget:'image'의 빈 상태와 같은 크기(결 통일). */
const PLACEHOLDER_HEIGHT = 112;
/** 채움 투명도 — legacy hazard-colors.ts의 rgba(r,g,b,0.20)와 동일. */
const FILL_ALPHA = 0.2;
/**
 * "박스가 배지보다 작다" 판정의 정규화 추정치 — legacy는 픽셀(24×20 + 여유)로 쟀지만 여기는
 * 표시 픽셀 크기를 모른다(% 배치). 통상 렌더 폭(300~700px)에서 배지 24px ≈ 4~8%라 8%로 추정.
 * 오판해도 배지가 박스 안/밖 어느 쪽엔 붙으므로 치명적이지 않다(단순화 허용 범위).
 */
const BADGE_EST_FRACTION = 0.08;

/** 값 없음 placeholder 박스 — widget:'image'의 빈 상태와 같은 크기(결 통일). image·photo
 *  readonly(composerWidgetTreeRender)도 이 한 벌을 공유한다(인라인 사본 금지 — 여기가 정본). */
export const IMAGE_PLACEHOLDER_STYLE: CSSProperties = {
  width: '100%',
  height: PLACEHOLDER_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#f9fafb',
  color: '#9ca3af',
  fontSize: '0.75rem',
};

/** 읽기 전용 이미지 카드 결(폭 채움·112·cover) — image 위젯·photo readonly·행 그리드 사진 칸이
 *  공유한다. placeholder 짝은 IMAGE_PLACEHOLDER_STYLE(바로 위) — 두 값이 한 파일에 있어야 크기
 *  결이 갈라지지 않는다(구 composerWidgetTreeRender 로컬 사본을 이리로 수렴, 사본 금지). */
export const READONLY_IMAGE_STYLE: CSSProperties = {
  width: '100%',
  height: PLACEHOLDER_HEIGHT,
  objectFit: 'cover',
  background: '#f9fafb',
};

// ── 썸네일 표시(displayVariant:'thumbnail') 한 벌 ────────────────────────────
// 구 TBM `HazardItemCard`의 사진(`w-[30%]` 래퍼 + `aspect-square object-cover rounded border`)
// 동형이다. 전폭 밴드가 어색한 자리(행마다 사진이 붙는 카드)에서만 선언으로 켠다 — 미지정 선언은
// 종전 전폭 그대로라 기존 사용처(안전점검보고서 개선 전/후 사진 등)에 회귀가 없다.
// 값은 여기가 정본(사본 금지) — image·annotated-image 두 위젯이 같은 폭을 쓴다.

/** 썸네일 래퍼 — 카드 폭의 30%. 안쪽은 100%를 채운다(이미지·placeholder 공통 좌표계). */
export const IMAGE_THUMBNAIL_WRAP_STYLE: CSSProperties = { width: '30%' };

/** 썸네일 이미지(widget:'image') — 정사각 crop. 테두리 색은 line 토큰(#e8ecf2) 전사. */
export const IMAGE_THUMBNAIL_IMG_STYLE: CSSProperties = {
  display: 'block',
  width: '100%',
  aspectRatio: '1 / 1',
  objectFit: 'cover',
  borderRadius: 6,
  border: '1px solid #e8ecf2',
  background: '#f9fafb',
};

/** 썸네일 빈 값 박스 — 전폭 placeholder를 같은 정사각 자리로 줄인 것(전폭 회색 밴드가 더 어색하다). */
export const IMAGE_THUMBNAIL_PLACEHOLDER_STYLE: CSSProperties = {
  ...IMAGE_PLACEHOLDER_STYLE,
  height: 'auto',
  aspectRatio: '1 / 1',
  borderRadius: 6,
  border: '1px solid #e8ecf2',
};

// ⚠️ widget:'image'는 고정 높이 112 + objectFit:'cover'(카드 결 통일이 우선이라 잘라 채운다).
// 주석 이미지는 그럴 수 없다 — 잘라내면 0~1 정규화 좌표가 가리키는 자리가 사라져 도형이 엉뚱한
// 데를 가리킨다. 그래서 폭을 채우되 **원본 비율 그대로**(height:auto) 전체를 보여준다 —
// 도형 없는 값(문자열 URL)도 같은 규칙이라 한 위젯 안에서 크기 규칙이 갈리지 않는다.
const IMG_STYLE: CSSProperties = {
  display: 'block',
  width: '100%',
  height: 'auto',
  background: '#f9fafb',
};

const OVERLAY_STYLE: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  // 표시 전용 오버레이 — 아래 이미지의 포인터 이벤트(확대 등 호스트 동작)를 막지 않는다.
  pointerEvents: 'none',
};

// img가 block + width:100%라 컨테이너 높이 = 이미지 높이가 되고, inset 0인 오버레이 레이어가 곧
// 이미지 영역이 된다 — 0~1 정규화 좌표가 이미지와 정확히 겹치는 근거(컨테이너가 이미지보다 크면 밀린다).
const CONTAINER_STYLE: CSSProperties = { position: 'relative', width: '100%' };

// ── maxHeight(상한) 지정 시의 shrink-wrap 구조 ──────────────────────────────
// 상한은 **비율 유지 축소**다(크롭 금지 — 잘라내면 0~1 좌표가 가리키는 자리가 사라진다).
// 축소되면 이미지 폭 < 부모 폭이 되므로, 오버레이 컨테이너는 width:100% 대신 이미지를
// shrink-wrap(fit-content)해야 inset 0 오버레이가 여전히 이미지 박스와 정확히 겹친다.
// 바깥 flex는 좁아진 이미지를 부모 폭 안에서 가운데 정렬한다(전폭 자리 유지).
const CAP_WRAP_STYLE: CSSProperties = { display: 'flex', justifyContent: 'center', width: '100%' };
const CAP_CONTAINER_STYLE: CSSProperties = {
  position: 'relative',
  width: 'fit-content',
  maxWidth: '100%',
};

/** 값이 AnnotatedImageValue 객체인지(문자열 URL 하위호환 분기용). */
export function isAnnotatedImageValue(value: unknown): value is AnnotatedImageValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    typeof (value as { url?: unknown }).url === 'string'
  );
}

/**
 * 값 → { url, shapes, strokes } 정규화. 문자열이면 URL만 있는 평면 이미지로 취급(하위호환),
 * 그 외(빈 값·형태 불일치)는 url 빈 문자열 = 값 없음.
 */
export function normalizeAnnotatedImageValue(value: unknown): AnnotatedImageValue {
  if (typeof value === 'string') return { url: value.trim() };
  if (isAnnotatedImageValue(value)) {
    return {
      url: value.url.trim(),
      shapes: Array.isArray(value.shapes) ? value.shapes : undefined,
      strokes: Array.isArray(value.strokes) ? value.strokes : undefined,
    };
  }
  return { url: '' };
}

/**
 * 0~1 정규화 좌표 → %(div)·viewBox 0~100(svg) 좌표. 비유한값은 0으로 접는다(깨진 도형이 렌더를
 * 막지 않게). 소수 2자리로 양자화 — 곱셈 부동소수 오차(0.1*100 = 10.000000000000002)가 그대로
 * 마크업에 새는 것을 막는다. 2자리 = 이미지 변의 1/10000이라 표시 정밀도에는 영향이 없다.
 */
const pct = (n: number): number => (Number.isFinite(n) ? Math.round(n * 10000) / 100 : 0);

/** 채움 대상 hex(#rgb/#rrggbb)만 허용 — 변환 정본(resolver hexToRgba)에 위임하기 전 게이트. */
const FILL_HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/**
 * #rgb/#rrggbb → rgba(r, g, b, alpha). 그 외 형식(색이름·rgba 문자열 등)은 undefined —
 * 채움을 생략한다(외곽선·배지는 원 문자열 그대로 쓰므로 표시 자체는 유지).
 * 변환 자체는 resolver hexToRgba(rc 정본)가 담당하고, 이 래퍼는 비hex 생략 게이트만 더한다.
 */
export function hexToRgba(hex: string, alpha: number): string | undefined {
  return FILL_HEX_RE.test(hex) ? hexToRgbaCss(hex, alpha) : undefined;
}

/** 노드 저작 옵션(ImageOverlayOptions의 스타일 축) — 전부 미지정이면 기본 렌더(legacy 결)와 동일.
 *  스타일 오버라이드는 **사각형(도형) 전용**이다 — 수기 선(strokes)은 그린 시점 데이터(자체
 *  색·두께)가 정본이라 어떤 오버라이드도 적용하지 않는다(2026-08-05 사용자 확정).
 *  AnnotatedImageViewProps가 이 5필드를 그대로 extends한다(동일 목록 이중 선언 금지). */
export interface OverlayRenderOptions {
  /** 사각형 외곽선 두께(px). 미지정=DEFAULT_STROKE_WIDTH. 수기 선에는 미적용(자체 값). */
  strokeWidth?: number;
  /** 지정 시 사각형 개별 색을 무시하고 이 색으로 강제. 수기 선에는 미적용(자체 값). */
  colorOverride?: string;
  /** false면 사각형 번호 배지 미렌더. 미지정=true. */
  showLabels?: boolean;
  /** 사각형 외곽선 스타일. 미지정='dashed'(legacy strokeDasharray '4,2' 결). */
  lineStyle?: 'dashed' | 'solid';
  /** false면 사각형 내부 반투명 채움 생략. 미지정=true(legacy rgba 0.20 채움). */
  showFill?: boolean;
}

/** 배지(#N) 스타일 — legacy 24×20 rect + 흰 bold 11px 대응(내용 길이에 맞춰 padding으로 근사). */
const BADGE_BASE_STYLE: CSSProperties = {
  position: 'absolute',
  color: '#ffffff',
  fontSize: 11,
  fontWeight: 700,
  lineHeight: '16px',
  padding: '2px 6px',
  borderRadius: 4,
  whiteSpace: 'nowrap',
};

function renderShape(shape: AnnotatedImageShape, index: number, opts: OverlayRenderOptions): ReactNode {
  if (shape.type !== 'rect') return null;
  const color = opts.colorOverride ?? shape.color ?? ANNOTATED_IMAGE_DEFAULT_COLOR;
  const box: CSSProperties = {
    position: 'absolute',
    left: `${pct(shape.x)}%`,
    top: `${pct(shape.y)}%`,
    width: `${pct(shape.w)}%`,
    height: `${pct(shape.h)}%`,
    border: `${opts.strokeWidth ?? DEFAULT_STROKE_WIDTH}px ${opts.lineStyle ?? 'dashed'} ${color}`,
    borderRadius: 4,
    boxSizing: 'border-box',
  };
  if (opts.showFill !== false) {
    const fill = hexToRgba(color, FILL_ALPHA);
    if (fill !== undefined) box.background = fill;
  }
  let badge: ReactNode = null;
  if (shape.label && opts.showLabels !== false) {
    // 배치는 legacy HazardOverlay 규칙의 % 단순화판: 기본은 박스 좌상단 모서리 겹침, 박스가
    // 배지보다 작으면(추정치) 박스 위 바깥, 위 여유가 없으면 아래 바깥.
    const tooSmall = shape.w < BADGE_EST_FRACTION || shape.h < BADGE_EST_FRACTION;
    const placement: CSSProperties = !tooSmall
      ? { top: 0, left: 0 }
      : shape.y >= BADGE_EST_FRACTION
        ? { bottom: '100%', left: 0, marginBottom: 2 }
        : { top: '100%', left: 0, marginTop: 2 };
    badge = (
      <span style={{ ...BADGE_BASE_STYLE, ...placement, background: color }}>{shape.label}</span>
    );
  }
  return (
    <div key={`shape-${index}`} style={box}>
      {badge}
    </div>
  );
}

function renderStroke(stroke: AnnotatedImageStroke, index: number): ReactNode {
  const points = Array.isArray(stroke.points) ? stroke.points : [];
  if (points.length < 2) return null;
  return (
    <polyline
      key={`stroke-${index}`}
      points={points.map((p) => `${pct(p.x)},${pct(p.y)}`).join(' ')}
      fill="none"
      // 수기 선 스타일은 그린 시점 데이터가 정본 — 노드 옵션(colorOverride/strokeWidth)을
      // 적용하지 않는다(사각형 전용, OverlayRenderOptions docblock 참조).
      stroke={stroke.color ?? ANNOTATED_IMAGE_DEFAULT_COLOR}
      strokeWidth={stroke.width ?? DEFAULT_STROKE_WIDTH}
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
    />
  );
}

/** 표시 props = 값/표시 부대 + 사각형 스타일 옵션(OverlayRenderOptions — 필드 상세는 그쪽 JSDoc). */
export interface AnnotatedImageViewProps extends OverlayRenderOptions {
  /** 값 — AnnotatedImageValue 객체 또는 URL 문자열(하위호환) */
  value: unknown;
  /** img alt(표시 라벨) */
  alt?: string;
  /** 값이 비었을 때 표시할 문구. 미지정 시 widget:'image'와 같은 '사진 없음' */
  placeholder?: string;
  /**
   * 최대 높이(px) — 이미지를 **비율 유지 축소**로 상한에 맞춘다(크롭 아님 — 0~1 좌표 안전).
   * 지정 시 shrink-wrap 구조(바깥 flex 중앙 + fit-content 오버레이 컨테이너)로 바뀌고,
   * 미지정이면 기존 마크업 그대로(전폭 원본비율 — 회귀 0). placeholder·평면 경로도 동일 상한.
   * body image 노드에서는 별도 축 없이 **layout.height 숫자값**이 이 prop으로 전달된다
   * (주석 이미지의 높이=상한 재해석 — rc ImagePrim annotated 분기, 2026-08-06 사용자 확정).
   */
  maxHeight?: number;
}

/**
 * 주석 이미지 1개를 표시한다(입력 아님).
 *  - 값 없음 → 회색 placeholder 박스(widget:'image'의 빈 상태와 같은 크기)
 *  - URL 문자열(또는 도형 0개) → 오버레이 없는 이미지
 *  - AnnotatedImageValue → 이미지 + 사각형 div 레이어(점선·채움·배지) + 수기선 svg
 * 크기는 어느 경우든 원본 비율 그대로(잘라내지 않음) — 이유는 IMG_STYLE 주석 참조.
 * maxHeight 지정 시에도 비율 유지 축소일 뿐 잘라내지 않는다(CAP_* 스타일 주석 참조).
 */
export function AnnotatedImageView({
  value,
  alt,
  placeholder,
  strokeWidth,
  colorOverride,
  showLabels,
  lineStyle,
  showFill,
  maxHeight,
}: AnnotatedImageViewProps) {
  const { url, shapes, strokes } = normalizeAnnotatedImageValue(value);
  const capped = maxHeight !== undefined;
  if (!url) {
    return (
      <div style={capped ? { ...IMAGE_PLACEHOLDER_STYLE, maxHeight } : IMAGE_PLACEHOLDER_STYLE}>
        {placeholder ?? '사진 없음'}
      </div>
    );
  }
  const opts: OverlayRenderOptions = { strokeWidth, colorOverride, showLabels, lineStyle, showFill };
  // rect 아닌 도형·점 1개짜리 선은 renderShape/renderStroke가 null을 돌려준다(무시하고 나머지는 렌더).
  const shapeEls: ReactNode[] = (shapes ?? [])
    .map((shape, i) => renderShape(shape, i, opts))
    .filter((el) => el !== null);
  const strokeEls: ReactNode[] = (strokes ?? [])
    .map((stroke, i) => renderStroke(stroke, i))
    .filter((el) => el !== null);
  // 로드 실패는 같은 발자국의 안내 칸으로 접는다(ImageWithFallback 헤더 — P6-d). 오버레이 도형은
  // 폴백 상태에서도 그대로 얹힌다 — 좌표가 가리킬 사진이 없을 뿐 「무엇을 지적했나」는 값에 남아 있다.
  const img = (
    <ImageWithFallback
      src={url}
      alt={alt ?? ''}
      style={
        capped
          ? // 상한 축소: 폭 강제(100%) 대신 양축 상한 + auto — 비율 유지로 maxHeight 안에 들어온다.
            {
              display: 'block',
              maxWidth: '100%',
              maxHeight,
              width: 'auto',
              height: 'auto',
              background: '#f9fafb',
            }
          : IMG_STYLE
      }
    />
  );
  // 도형이 하나도 없으면 오버레이 레이어 자체를 만들지 않는다(평면 이미지와 완전 동일 마크업).
  // 상한 지정 시에는 평면도 같은 flex 래퍼로 감싼다(축소된 이미지의 가운데 정렬 일관).
  if (shapeEls.length === 0 && strokeEls.length === 0) {
    return capped ? <div style={CAP_WRAP_STYLE}>{img}</div> : img;
  }
  const overlaid = (
    <div style={capped ? CAP_CONTAINER_STYLE : CONTAINER_STYLE}>
      {img}
      {strokeEls.length > 0 ? (
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={OVERLAY_STYLE}
          aria-hidden="true"
        >
          {strokeEls}
        </svg>
      ) : null}
      {shapeEls.length > 0 ? (
        <div style={OVERLAY_STYLE} aria-hidden="true">
          {shapeEls}
        </div>
      ) : null}
    </div>
  );
  return capped ? <div style={CAP_WRAP_STYLE}>{overlaid}</div> : overlaid;
}

export default AnnotatedImageView;
